import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MessageCircle } from "lucide-react";
import { fmt } from "@/hooks/useRates";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ p, index, enquireLabel }) => (
  <motion.div
    data-testid={`product-card-${p.id}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className="group relative overflow-hidden border border-white/10 bg-[#121212] hover:border-[#25D366]/60 transition-colors duration-500"
  >
    <div className="aspect-square overflow-hidden bg-[#181818] flex items-center justify-center">
      {p.image ? (
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
        />
      ) : (
        <span className="font-display font-black uppercase text-3xl text-white/10">{p.name.slice(0, 2)}</span>
      )}
    </div>
    <div className="p-3 sm:p-5">
      <p className="font-ticker text-[8px] sm:text-[9px] tracking-[0.3em] uppercase text-white/35">{p.category}</p>
      <p className="mt-1 font-display font-extrabold uppercase tracking-tight text-sm sm:text-lg text-[#FDFDFD] leading-tight">{p.name}</p>
      <p className="mt-1 font-ticker text-sm sm:text-base font-semibold text-[#25D366]" data-testid={`product-price-${p.id}`}>
        ₹{fmt(p.price, 2)} <span className="text-[9px] sm:text-[10px] uppercase text-white/35">{p.unit}</span>
      </p>
      <a
        href={`https://wa.me/918000093073?text=${encodeURIComponent(`Namaste Mahima Metals, mujhe ${p.name} chahiye. Rate ₹${p.price} ${p.unit} — please confirm.`)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`product-enquire-${p.id}`}
        className="mt-3 sm:mt-4 inline-flex w-full items-center justify-center gap-1.5 sm:gap-2 border border-[#25D366]/50 px-2 sm:px-4 py-2.5 font-ticker text-[9px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#25D366] hover:bg-[#25D366] hover:text-[#0A0A0A] transition-colors duration-300"
      >
        <MessageCircle size={13} className="shrink-0" /> <span className="truncate">{enquireLabel}</span>
      </a>
    </div>
  </motion.div>
);

export default function ProductsSection() {
  const { str } = useLang();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("ALUMINIUM");

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => setProducts(res.data.products)).catch(() => {});
  }, []);

  const CAT_ORDER = ["ALUMINIUM", "COPPER", "SOLAR", "LITHIUM", "E-WASTE", "BRASS", "SS", "MS", "OTHER"];
  const present = [...new Set(products.map((p) => p.category))];
  const cats = [
    ...CAT_ORDER.filter((c) => present.includes(c)),
    ...present.filter((c) => !CAT_ORDER.includes(c)),
  ];
  const shown = products.filter((p) => p.category === cat);

  return (
    <section id="products" data-testid="products-section" className="relative border-b border-white/10">
      <div className="px-6 sm:px-10 lg:px-16 pt-24 lg:pt-32 pb-12">
        <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">{str.productsTag}</p>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
          {str.productsH1} <span className="text-stroke-gold">{str.productsH2}</span>
        </h2>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-white/50">{str.productsPara}</p>
      </div>

      <div className="px-6 sm:px-10 lg:px-16 pb-24 lg:pb-32">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="product-category-tabs">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`product-tab-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`px-5 py-2.5 font-ticker text-[10px] tracking-[0.25em] uppercase border transition-colors duration-300 ${
                cat === c
                  ? "bg-[#25D366] border-[#25D366] text-[#0A0A0A] font-semibold"
                  : "border-white/15 text-white/55 hover:border-[#25D366] hover:text-[#25D366]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="products-grid">
          {shown.map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} enquireLabel={str.enquireWa} />
          ))}
        </div>
      </div>
    </section>
  );
}
